import React, { useEffect } from "react";
import { Select } from "@shared/ui/Select";
import { Input } from "@shared/ui/Input";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "next-i18next";
import { HorizontalFormField } from "@shared/ui/HorizontalFormField";
import {
  CreditTypeEnum,
  IApplicationForm,
  IApplicationFormStep3,
} from "@entities/Product/Application/types";
import {
  calcMonthlyPaid,
  extractNumbers,
  setSpacesInNumber,
} from "@shared/lib";
import { CreditCarFields } from "@entities/Product/Application/ui/CreditCarFields";
import { findOption } from "@entities/Product/Application/utils";
import { ISelectOption } from "@shared/types";

interface IProps {
  periodOptions: { value: string; label: string }[];
  creditAimOptions: { value: string; label: string }[];
  creditTypeOptions: { value: string; label: string }[];
  carBrandOptions: { value: string; label: string }[];
}

export function Step3Fields({
  periodOptions,
  creditAimOptions,
  creditTypeOptions,
  carBrandOptions,
}: IProps) {
  const { t } = useTranslation();
  const methods = useFormContext<IApplicationFormStep3 | IApplicationForm>();

  useEffect(() => {
    const { credit_type, credit_period, credit_sum } = methods.getValues();

    if (credit_type && credit_period && credit_sum) {
      const creditOption = findOption(creditTypeOptions, credit_type);
      const periodOption = findOption(periodOptions, credit_period);
      const period = extractNumbers(periodOption.label);
      const rate = creditOption?.loan_rate;
      const sum = extractNumbers(credit_sum);

      methods.setValue(
        "credit_monthly_payment",
        String(setSpacesInNumber(Math.ceil(calcMonthlyPaid(period, sum, rate))))
      );
    }
  }, [
    methods.watch("credit_type"),
    methods.watch("credit_period"),
    methods.watch("credit_sum"),
  ]);

  useEffect(() => {
    const { credit_sum, credit_car_price } = methods.watch();

    if (credit_sum && credit_car_price) {
      const value =
        Number(extractNumbers(credit_car_price)) -
        Number(extractNumbers(credit_sum));

      methods.setValue(
        "advance_payment",
        String(value > 0 ? setSpacesInNumber(value) : 0)
      );
    }
  }, [methods.watch("credit_sum"), methods.watch("credit_car_price")]);

  return (
    <>
      <HorizontalFormField label={t(`Вид кредитования`)}>
        <Controller
          control={methods.control}
          name="credit_type"
          render={({ field, fieldState }) => (
            <Select
              value={findOption(creditTypeOptions, field.value)}
              onChange={(newValue: ISelectOption) =>
                methods.setValue("credit_type", newValue.value)
              }
              hasError={Boolean(fieldState.error)}
              errorMessage={fieldState.error?.message}
              options={creditTypeOptions}
              placeholder={t(`Выберите вид кредитования`)}
              noOptionsMessage={() => t(`Не найдено`)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Сумма микрокредита")}>
        <Controller
          name="credit_sum"
          control={methods.control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              onChange={(e) =>
                field.onChange(
                  setSpacesInNumber(extractNumbers(e.target.value))
                )
              }
              inputMode="numeric"
              hasError={Boolean(fieldState.error?.message)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Срок микрокредита")}>
        <Controller
          control={methods.control}
          name="credit_period"
          render={({ field, fieldState }) => (
            <Select
              value={findOption(periodOptions, field.value)}
              onChange={(newValue: ISelectOption) =>
                methods.setValue("credit_period", newValue.value)
              }
              hasError={Boolean(fieldState.error)}
              errorMessage={fieldState.error?.message}
              options={periodOptions}
              placeholder={t(`Выберите период`)}
              noOptionsMessage={() => t(`Не найдено`)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Ежемесячный платеж")}>
        <Controller
          name="credit_monthly_payment"
          control={methods.control}
          render={({ field }) => (
            <Input
              {...field}
              onChange={(e) =>
                field.onChange(
                  setSpacesInNumber(extractNumbers(e.target.value))
                )
              }
              disabled
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Цель микрокредита")}>
        <Controller
          control={methods.control}
          name="credit_target"
          render={({ field, fieldState }) => (
            <Select
              value={findOption(creditAimOptions, field.value)}
              onChange={(newValue: ISelectOption) => {
                field.onChange(newValue.value);
                methods.trigger("credit_target");
              }}
              hasError={Boolean(fieldState.error)}
              errorMessage={fieldState.error?.message}
              options={creditAimOptions}
              placeholder={t(`Выберите цель`)}
              noOptionsMessage={() => t(`Не найдено`)}
            />
          )}
        />
      </HorizontalFormField>

      {[CreditTypeEnum.NEW_AUTO, CreditTypeEnum.USED_AUTO].includes(
        methods.watch("credit_type") as CreditTypeEnum
      ) && <CreditCarFields brandOptions={carBrandOptions} />}
    </>
  );
}
